import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import type { FeeStructureOption } from "../../interface/profile/FeeStructureOption";

interface AnalystProfileSettingsProps {
  feeStructureOptions?: FeeStructureOption[];
  consultationAvailable?: boolean;
  consultationDuration?: number;
  acceptingNewClients?: boolean;
}

const formatFeeValue = (fee: FeeStructureOption) => {
  const parts: string[] = [];
  if (fee.aumPercentage != null) parts.push(fee.aumPercentage > 100 ? `$${fee.aumPercentage}` : `${fee.aumPercentage}%`);
  if (fee.hourlyRate != null) parts.push(`$${fee.hourlyRate}/hr`);
  if (fee.flatFeeAmount != null) parts.push(`$${fee.flatFeeAmount}`);
  if (fee.performanceFeePercentage != null) parts.push(`${fee.performanceFeePercentage}% perf`);
  if (fee.minimumFee != null) parts.push(`min $${fee.minimumFee}`);
  return parts.join(" | ");
};

export function AnalystProfileSettings({ feeStructureOptions, consultationAvailable, consultationDuration, acceptingNewClients }: AnalystProfileSettingsProps) {
  const fees = feeStructureOptions || [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Analyst Profile Settings</h3>
        <p className="text-sm text-muted-foreground">
          Configure your professional profile and service offerings
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Fee Structures</CardTitle>
          <CardDescription>Your configured pricing models</CardDescription>
        </CardHeader>
        <CardContent>
          {fees.length > 0 ? (
            <div className="space-y-3">
              {fees.map((fee) => (
                <div key={fee.id} className="p-4 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium">{fee.name}</span>
                    <Badge variant="secondary" className="text-xs">{fee.feeType.replace(/_/g, " ")}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">{formatFeeValue(fee)}</div>
                  {fee.billingFrequency && <p className="text-xs text-muted-foreground mt-1">Billing: {fee.billingFrequency}</p>}
                  {fee.description && <p className="text-xs italic mt-1">{fee.description}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No fee structures configured</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Consultation Settings</CardTitle>
          <CardDescription>Client consultation preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Free Initial Consultation</p>
              <p className="text-xs text-muted-foreground">Offer a complimentary first meeting</p>
            </div>
            <Badge variant={consultationAvailable ? "default" : "secondary"}>
              {consultationAvailable ? "Available" : "Not Offered"}
            </Badge>
          </div>
          {consultationAvailable && consultationDuration != null && (
            <p className="text-sm text-muted-foreground">{consultationDuration} minute consultation</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Client Availability</CardTitle>
          <CardDescription>New client acceptance status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Accepting New Clients</p>
              <p className="text-xs text-muted-foreground">Display your profile to potential clients</p>
            </div>
            <Badge variant={acceptingNewClients ? "default" : "secondary"}>
              {acceptingNewClients ? "Accepting" : "Not Accepting"}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
